/**
 * API Configuration
 * 
 * This module provides utilities for constructing API URLs based on environment variables.
 * Supports local development, staging, and production environments.
 */

import { logout, isTokenExpired, getAccessToken } from "../utils/auth";

/**
 * Get the backend URL based on environment variables
 */
const getBackendUrl = (): string => {
  // In development, use environment variable or fallback to localhost
  if (process.env.NODE_ENV === 'development') {
    return process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001';
  }
  
  // In production/staging, use environment variable (set by Vercel)
  // This should be set in Vercel environment variables
  return process.env.REACT_APP_BACKEND_URL || process.env.REACT_APP_API_URL || '';
};

/**
 * Base URL for API requests
 */
export const API_BASE_URL = getBackendUrl();

/**
 * Construct a full API URL from a path
 * 
 * @param path - API path (e.g., "/api/invoices" or "api/invoices")
 * @returns Full URL (e.g., "https://api.yourdomain.com/api/invoices")
 * 
 * @example
 * getApiUrl("/api/invoices") // "https://api.yourdomain.com/api/invoices"
 * getApiUrl("api/invoices")  // "https://api.yourdomain.com/api/invoices"
 * getApiUrl("https://external.com/api") // "https://external.com/api" (unchanged)
 */
export const getApiUrl = (path: string): string => {
  // If path is already a full URL, return as is
  if (path.startsWith('http://') || path.startsWith('https://')) {
    return path;
  }
  
  // Remove leading slash if present
  const cleanPath = path.startsWith('/') ? path.slice(1) : path;
  
  // Combine base URL and path
  return `${API_BASE_URL}/${cleanPath}`;
};

/**
 * Get authentication token from local storage
 */
export const getAuthToken = (): string | null => {
  return localStorage.getItem("accessToken");
};

/**
 * Helper for making authenticated requests with optional caching
 */
const responseCache = new Map<string, { data: any; expires: number }>();
let isRedirectingToLogin = false;

export const authenticatedFetch = async (
  input: RequestInfo,
  init?: RequestInit & { cacheKey?: string; ttl?: number }
): Promise<Response> => {
  let token = getAuthToken();

  // Proactive check before request
  if (token && isTokenExpired(token)) {
    console.warn("Token expired before request, clearing session...");
    
    // Always clear the token if it's expired
    await logout();
    token = null; // Don't send expired token in headers

    const path = window.location.pathname;
    const isLandingPage =
      path === "/" ||
      path.endsWith("/signup") ||
      path.endsWith("/login") ||
      path.includes("/verify-email") ||
      path.includes("/reset-password");

    if (!isLandingPage && !isRedirectingToLogin) {
      isRedirectingToLogin = true;
      const localeMatch = path.match(/^\/([a-z]{2})(\/|$)/);
      const locale = localeMatch ? localeMatch[1] : "en";
      window.location.href = `/${locale}`;
      return new Response(null, { status: 401 });
    }
  }

  const headers: HeadersInit = {
    ...init?.headers,
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    "Cache-Control": "no-store",
  };

  let url = input;
  let fullUrl = "";
  if (typeof input === "string") {
    url = getApiUrl(input);
    fullUrl = url;
  } else if (input instanceof URL) {
    fullUrl = input.toString();
  } else {
    fullUrl = input.url;
  }

  // If a cacheKey is provided, check the cache first
  if ((init?.cacheKey && init.method === "GET") || (!init?.method && !init?.body)) {
    const key = init?.cacheKey || fullUrl;
    const cached = responseCache.get(key);
    if (cached && cached.expires > Date.now()) {
      return new Response(JSON.stringify(cached.data), {
        status: 200,
        headers: { "Content-Type": "application/json" },
      });
    }
  }

  const response = await fetch(url, {
    ...init,
    cache: init?.cache ?? "no-store",
    headers,
  });

  // Handle 401 Unauthorized globally
  if (response.status === 401 && !isRedirectingToLogin) {
    console.warn("Global 401 detected, logging out...");
    
    // Check if we're already on the landing page to avoid infinite redirect loops
    const path = window.location.pathname;
    const isLandingPage = 
      path === "/" || 
      path.endsWith("/signup") || 
      path.endsWith("/login") || 
      path.includes("/verify-email") ||
      path.includes("/reset-password");
    
    if (!isLandingPage) {
      isRedirectingToLogin = true;
      // Clear token and redirect
      await logout();
      
      // Determine locale for redirect
      const localeMatch = path.match(/^\/([a-z]{2})(\/|$)/);
      const locale = localeMatch ? localeMatch[1] : "en";
      
      // Redirect to login/landing
      window.location.href = `/${locale}`;
      
      // Keep isRedirectingToLogin true to prevent further redirects during page transition
    }
  }

  // If successful and cacheable, store in cache
  if (response.ok && init?.ttl && ((init.method === "GET") || (!init.method && !init.body))) {
    const key = init?.cacheKey || fullUrl;
    const clonedResponse = response.clone();
    try {
      const data = await clonedResponse.json();
      responseCache.set(key, {
        data,
        expires: Date.now() + init.ttl,
      });
    } catch (e) {
      // Not JSON, don't cache
    }
  }

  return response;
};

/**
 * Create a Stripe Checkout Session
 */
export const createStripeCheckoutSession = async (
  plan: 'monthly' | 'yearly' | 'lifetime',
  opts?: { successPath?: string; cancelPath?: string }
): Promise<{ url: string }> => {
  const response = await authenticatedFetch('/api/stripe/create-checkout-session', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ plan, ...opts }),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || 'Failed to create checkout session');
  }

  return response.json();
};

/**
 * Create Stripe Customer Portal session (manage/cancel subscription)
 */
export const createStripePortalSession = async (): Promise<{ url: string }> => {
  const response = await authenticatedFetch('/api/stripe/create-portal-session', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || 'Failed to create customer portal session');
  }

  return response.json();
};

/**
 * Check if we're in development mode
 */
export const isDevelopment = (): boolean => {
  return process.env.NODE_ENV === 'development';
};

/**
 * Check if we're in production mode
 */
export const isProduction = (): boolean => {
  return process.env.NODE_ENV === 'production';
};
